import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./HarvardCitationContent.css";

// ---- tiny scroll-reveal wrapper (mirrors the reference design's fade-up-on-scroll) ----
const Reveal = ({ as: Tag = "div", className = "", style, children, id }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <Tag
      ref={ref}
      id={id}
      style={style}
      className={`hv-reveal${visible ? " hv-visible" : ""}${className ? ` ${className}` : ""}`}
    >
      {children}
    </Tag>
  );
};

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const GlobeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <path d="M2 12h20M12 2a15 15 0 0 1 0 20 15 15 0 0 1 0-20z" />
  </svg>
);

const BookIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20V4H6.5A2.5 2.5 0 0 0 4 6.5v13z" />
    <path d="M6.5 17H20v3H6.5A2.5 2.5 0 0 1 4 17.5 2.5 2.5 0 0 1 6.5 17z" />
  </svg>
);

const DocIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <path d="M14 2v6h6" />
  </svg>
);

const VideoIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polygon points="23 7 16 12 23 17 23 7" />
    <rect x="1" y="5" width="15" height="14" rx="2" />
  </svg>
);

const NewspaperIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 22h16V6l-4-4H4z" />
    <path d="M14 2v6h6" />
  </svg>
);

const ReportIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M9 12h6M9 16h6M9 8h6M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z" />
  </svg>
);

const EditIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
  </svg>
);

const GridIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M16 2v4M8 2v4M3 10h18" />
  </svg>
);

const FileCheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const ListCheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M9 11l3 3L22 4" />
    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
  </svg>
);

const HV_SOURCE_TYPES = [
  { tag: "Website", title: "Harvard Website Citation", desc: "Use this when your source is a webpage, blog post, or online article with no fixed print edition. The reference includes the author or organisation, publication year, page title, and an access date.", chips: ["Author-date format", "Access date required"] },
  { tag: "Book", title: "Harvard Book Citation", desc: "Use this for print or ebook chapters, textbooks, and edited collections. The reference records the author, year of publication, title in italics, edition where relevant, and the publisher.", chips: ["Publisher required", "Edition optional"] },
  { tag: "Journal Article", title: "Harvard Journal Citation", desc: "Use this for peer-reviewed research. The reference lists the author, year, article title in single quotation marks, the journal name in italics, volume, issue, and page range.", chips: ["Volume & issue", "Page range"] },
  { tag: "Video", title: "Harvard Video Citation", desc: "Use this for lectures, documentaries, and online video content. The reference notes the creator or channel, year uploaded, title, the platform it is hosted on, and the date you accessed it.", chips: ["Platform name", "Access date"] },
];

const HV_STEPS = [
  { title: "Choose your source type", desc: "Select website, book, journal article, or video so the generator asks for the right details." },
  { title: "Paste the source details", desc: "Add a URL or DOI where you have one, or enter the author, year, and title yourself." },
  { title: "Check the fields", desc: "Confirm the author, year, and title are spelled and capitalised the way they appear in the original source." },
  { title: "Review the citation", desc: "Read the generated reference and in-text citation, and check the punctuation matches Harvard citation style." },
  { title: "Copy it into your work", desc: "Add the in-text citation where you refer to the source, and the full reference to your reference list." },
];

const HV_ACTION_CARDS = [
  { icon: <GlobeIcon />, title: "Website Citation Generator", desc: "Cite webpages, blogs, and digital articles in Harvard style." },
  { icon: <BookIcon />, title: "Book Citation Generator", desc: "Create references for print books, ebooks, and chapters." },
  { icon: <DocIcon />, title: "Journal Article Citation Generator", desc: "Generate citations for scholarly and peer-reviewed papers." },
  { icon: <VideoIcon />, title: "Video Citation Generator", desc: "Cite video lectures, documentaries, and online recordings." },
  { icon: <NewspaperIcon />, title: "Newspaper Article Citation Generator", desc: "Create citations for print and online newspaper articles." },
  { icon: <ReportIcon />, title: "Report Citation Generator", desc: "Cite research reports, white papers, and government documents." },
];

const HV_EXAMPLES = [
  {
    tag: "Journal Article · Harvard",
    ref: "Smith, J. and Lee, A. (2023) 'Understanding AI research', Journal of Technology, 35(3), pp. 100–115.",
    intext: "(Smith and Lee, 2023)",
  },
  {
    tag: "Website · Harvard",
    ref: "ResearchPal (2025) How AI tools help students write better. Available at: researchpal.co (Accessed: 3 August 2026).",
    intext: "(ResearchPal, 2025)",
  },
];

const HV_WHY_ITEMS = [
  { icon: <EditIcon />, title: "Style-Accurate Formatting", desc: "Punctuation, italics, and bracket placement follow Harvard citation style rules." },
  { icon: <GridIcon />, title: "Source-Type Fields", desc: "Websites, books, journals, and videos each ask for the details they actually need." },
  { icon: <EditIcon />, title: "Manual Editing", desc: "Adjust any field yourself for incomplete or unusual source details." },
  { icon: <FileCheckIcon />, title: "Author-Date In-Text Citations", desc: "Every reference comes paired with the matching in-text citation Harvard style requires." },
  { icon: <ListCheckIcon />, title: "Full Reference List Output", desc: "Generated entries drop straight into your reference list in the correct order." },
  { icon: <BookIcon />, title: "Bibliography Support", desc: "Keep track of sources for longer, multi-source projects." },
];

const HV_PLAN_COMPARISON = [
  { feature: "Generate Harvard citations", free: true, premium: true },
  { feature: "Full author-date reference list entries", free: true, premium: true },
  { feature: "Copy citations", free: true, premium: true },
  { feature: "Save bibliography lists", free: "limited", premium: "Unlimited" },
  { feature: "Export to Word or Google Docs", free: "limited", premium: true },
  { feature: "Bulk citation import", free: null, premium: true },
];

const HV_FAQS = [
  { q: "What is a Harvard citation generator?", a: "A Harvard citation generator is a tool that turns source details, such as an author, year, and title, into a properly formatted Harvard reference and its matching in-text citation, so you do not have to arrange the punctuation and order by hand." },
  { q: "How do I use the Harvard citation generator to reference a source?", a: "Choose the source type, then paste a URL or DOI, or fill in the author, year, and title yourself. The tool then builds the full reference and shows the in-text citation you can drop into your writing." },
  { q: "Is this Harvard style citation generator free to use?", a: "Yes. Generating a Harvard citation, viewing the in-text version, and copying the result are all free. Saving longer bibliography lists and exporting to Word or Google Docs are part of the premium plan." },
  { q: "What is the difference between Harvard citation and Harvard referencing?", a: "Harvard citation usually refers to the short in-text note, such as (Smith, 2024), while Harvard referencing describes the full reference list entry at the end of your document. Both follow the same author-date system." },
  { q: "Which source types can I cite in Harvard style with this tool?", a: "You can currently generate Harvard references for websites, books, journal articles, and videos, with fields adjusted to match the details each source type needs." },
  { q: "How do I write an in-text citation in Harvard style?", a: "Place the author's surname and the year of publication in brackets where you refer to the source, for example (Smith, 2024). If you quote directly, add the page number after the year." },
  { q: "What does a full Harvard reference list entry contain?", a: "A typical entry lists the author, year of publication, title, and details specific to the source type, such as a publisher for books or a journal name, volume, and page range for articles." },
  { q: "Does the free Harvard citation generator require sign-up?", a: "No. You can generate and copy a citation without creating an account. An account is only needed if you want to save bibliography lists across sessions." },
  { q: "Can I generate a Harvard style citation from a DOI or URL?", a: "Yes. Pasting a URL or DOI into the source details field lets the tool pull through the relevant information for that source, which you can then review and adjust." },
  { q: "How is Harvard referencing different from APA or MLA?", a: "Harvard, APA, and MLA are all author-date or author-page systems, but they differ in punctuation, capitalisation, and how the reference list is titled and ordered. Harvard reference lists are commonly labelled simply as a reference list or bibliography." },
  { q: "What order do I list sources in a Harvard reference list?", a: "Sources are listed alphabetically by the author's surname or, where there is no named author, by the organisation or title. Multiple works by the same author are then ordered by year." },
  { q: "How do I cite a source with no listed author in Harvard style?", a: "Use the organisation responsible for the content in place of an author, for example a company or government department. If neither exists, the title of the source can take that position instead." },
  { q: "Can I cite a website using the Harvard referencing citation generator?", a: "Yes. Select the website source type, add the author or organisation, the year, the page title, and the URL, and the generator will include an access date automatically." },
  { q: "How do I do a citation Harvard style for a journal article?", a: "Select the journal article source type and enter the author, year, article title, journal name, volume and issue, and the page range, and the tool will format these into a single reference entry." },
  { q: "What punctuation does Harvard style use between citation elements?", a: "Author and year are separated by a space and enclosed in brackets for in-text citations. In the reference list, commas separate most elements, article titles sit in single quotation marks, and book or journal titles appear in italics." },
  { q: "Can I export my Harvard citations to Word or Google Docs?", a: "Exporting citations directly to Word or Google Docs is available on the premium plan. On the free plan, you can copy a citation and paste it into any document yourself." },
  { q: "How do I cite multiple works by the same author in Harvard style?", a: "List each work separately in the reference list, ordered by year. If two works share the same author and year, add a lowercase letter after the year, such as 2024a and 2024b, in both the in-text citation and the reference list." },
  { q: "Do different universities use slightly different Harvard citation rules?", a: "Some departments follow slightly different conventions for punctuation or the order of details, so it is worth checking your course handbook alongside any generated citation." },
  { q: "Should I double check citations generated by any citation tool?", a: "Yes. A generator formats the details you provide, but it cannot confirm that the author, year, or title you entered are correct, so a quick check against the original source is always worth the extra minute." },
  { q: "Can I save a bibliography list for a longer project?", a: "Yes. Saving a running bibliography list across a longer project is supported, with a limited number of saved entries on the free plan and unlimited saving on the premium plan." },
];

const FAQ_BATCH = 5;

const HvFaqList = () => {
  const [revealed, setRevealed] = useState(FAQ_BATCH);
  const [openSet, setOpenSet] = useState(() => new Set());

  const toggle = (idx) => {
    setOpenSet((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
    setRevealed((prev) => (prev < HV_FAQS.length ? Math.min(prev + FAQ_BATCH, HV_FAQS.length) : prev));
  };

  const visible = HV_FAQS.slice(0, revealed);
  const done = revealed >= HV_FAQS.length;

  return (
    <>
      <div className="hv-faq-list">
        {visible.map((item, idx) => {
          const open = openSet.has(idx);
          return (
            <div className={`hv-faq-item${open ? " hv-open" : ""}`} key={item.q}>
              <button type="button" className="hv-faq-q" aria-expanded={open} onClick={() => toggle(idx)}>
                {item.q}
                <span className="hv-plus" />
              </button>
              <div className="hv-faq-a">
                <div className="hv-faq-a-inner">{item.a}</div>
              </div>
            </div>
          );
        })}
      </div>
      <p className="hv-faq-more-note">
        {done ? "That is every question we have on Harvard referencing and citation." : "Open a question above to reveal the next set of questions."}
      </p>
    </>
  );
};

const HarvardCitationContent = () => {
  return (
    <div className="hv-lp">
      <section className="hv-section">
        <div className="hv-wrap">
          <Reveal className="hv-intro">
            <h1>Free Harvard Citation Generator: Author-Date References & In-Text Citations</h1>
            <p className="hv-lead">Need to reference a book, journal article, website, or video the way your department expects? This Harvard citation generator turns raw source details into a properly formatted Harvard style citation in seconds, without you having to memorise where every comma and italic belongs. Enter a URL, DOI, or the source details yourself, and get a full reference list entry plus the in-text citation Harvard style requires, ready to paste straight into your work.</p>
            <div className="hv-cta-row">
              <a href="#citation-tool" className="hv-btn hv-btn-primary">Generate My Citation Free</a>
              <a href="#hv-sources" className="hv-btn hv-btn-ghost">View Source Types</a>
            </div>
            <ul className="hv-feature-pills">
              <li><CheckIcon />Create citations faster</li>
              <li><CheckIcon />Accurate author-date formatting</li>
              <li><CheckIcon />Build in-text citations</li>
              <li><CheckIcon />Save time on formatting</li>
              <li><CheckIcon />Edit source details manually</li>
              <li><CheckIcon />Copy, save, or export</li>
            </ul>
          </Reveal>
        </div>
      </section>

      <section className="hv-section hv-section-soft" id="hv-basics">
        <div className="hv-wrap">
          <Reveal className="hv-section-head">
            <span className="hv-eyebrow">The Basics</span>
            <h2>What Is Harvard Referencing?</h2>
          </Reveal>
          <Reveal className="hv-quote-card">
            <p>Harvard referencing and citation is an author-date system: the in-text citation names the author and year, and the reference list at the end spells out the rest of the source in full.</p>
          </Reveal>
          <Reveal className="hv-two-col">
            <div className="hv-info-card">
              <h3>How Harvard Citation Style Works</h3>
              <p>Instead of arranging every detail by hand, you give the generator the author, year, title, and source details, and it places them in the order Harvard referencing rules expect, with the right brackets, punctuation, and italics applied automatically.</p>
            </div>
            <div className="hv-info-card">
              <h3>Why It Helps Your Reference List</h3>
              <p>A generator will not read your sources for you, but it does keep every entry in your reference list consistent, from the first citation in Harvard style to the last. Consistent formatting is one of the easiest marks to lose, and one of the easiest to keep.</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="hv-section" id="hv-sources">
        <div className="hv-wrap">
          <Reveal className="hv-section-head">
            <span className="hv-eyebrow">Pick Your Source</span>
            <h2>Which Sources Can You Cite in Harvard?</h2>
            <p>Different source types carry different details, so a Harvard style citation generator needs to ask for the right fields each time.</p>
          </Reveal>
          <Reveal className="hv-card-grid">
            {HV_SOURCE_TYPES.map((item) => (
              <div className="hv-source-card" key={item.tag}>
                <span className="hv-tag">{item.tag}</span>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
                <div className="hv-chip-row">{item.chips.map((c) => <span className="hv-chip" key={c}>{c}</span>)}</div>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      <section className="hv-section hv-section-soft" id="hv-how-to">
        <div className="hv-wrap">
          <Reveal className="hv-section-head">
            <span className="hv-eyebrow">Five Simple Steps</span>
            <h2>How to Do a Citation Harvard Style</h2>
            <p>How to use Harvard citation tools without second-guessing the result, in five short steps.</p>
          </Reveal>
          <div className="hv-steps">
            {HV_STEPS.map((step, idx) => (
              <Reveal as="div" className="hv-step" key={step.title}>
                <div className="hv-step-num">{idx + 1}</div>
                <div><h3>{step.title}</h3><p>{step.desc}</p></div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="hv-section">
        <div className="hv-wrap">
          <Reveal className="hv-section-head">
            <span className="hv-eyebrow">By Source</span>
            <h2>Generate a Harvard Citation by Source Type</h2>
          </Reveal>
          <Reveal className="hv-action-grid">
            {HV_ACTION_CARDS.map((c) => (
              <div className="hv-action-card" key={c.title}>
                <div className="hv-icon">{c.icon}</div>
                <h3>{c.title}</h3>
                <p>{c.desc}</p>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      <section className="hv-section hv-section-soft">
        <div className="hv-wrap">
          <Reveal className="hv-section-head">
            <span className="hv-eyebrow">See It In Action</span>
            <h2>Example Harvard Citations</h2>
          </Reveal>
          <Reveal className="hv-example-grid">
            {HV_EXAMPLES.map((ex) => (
              <div className="hv-example-card" key={ex.tag}>
                <div className="hv-example-tag">{ex.tag}</div>
                <div className="hv-example-ref">{ex.ref}</div>
                <div className="hv-example-intext">In-text citation: <strong>{ex.intext}</strong></div>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      <section className="hv-section">
        <div className="hv-wrap">
          <Reveal className="hv-section-head">
            <span className="hv-eyebrow">The Payoff</span>
            <h2>Why Use Our Free Harvard Citation Generator?</h2>
          </Reveal>
          <Reveal className="hv-feature-grid">
            {HV_WHY_ITEMS.map((w) => (
              <div className="hv-feature-item" key={w.title}>
                <div className="hv-icon">{w.icon}</div>
                <h3>{w.title}</h3>
                <p>{w.desc}</p>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      <section className="hv-section hv-section-soft" id="hv-pricing">
        <div className="hv-wrap">
          <Reveal className="hv-section-head">
            <span className="hv-eyebrow">Compare Plans</span>
            <h2>Free and Premium Features</h2>
          </Reveal>
          <Reveal className="hv-price-table-wrap">
            <table>
              <thead>
                <tr><th>Feature</th><th>Free Version</th><th>Premium Version</th></tr>
              </thead>
              <tbody>
                {HV_PLAN_COMPARISON.map((row) => (
                  <tr key={row.feature}>
                    <td>{row.feature}</td>
                    <td className={row.free === true ? "hv-yes" : row.free === "limited" ? "hv-lim" : "hv-no"}>
                      {row.free === true ? "Yes" : row.free === "limited" ? "Limited" : "—"}
                    </td>
                    <td className="hv-yes">
                      {row.premium === true ? "Yes" : row.premium}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Reveal>
        </div>
      </section>

      <section className="hv-section" id="hv-ai-research-assistant">
        <div className="hv-wrap">
          <Reveal className="hv-section-head">
            <span className="hv-eyebrow">AI Research Assistant</span>
            <h2>AI Research Assistant for Harvard Referencing</h2>
          </Reveal>
          <Reveal className="hv-two-col">
            <div className="hv-info-card">
              <p style={{ marginBottom: "14px" }}>Harvard referencing uses an author-date approach, but academic research involves much more than placing an author's name and publication year in the right position.</p>
              <p style={{ marginBottom: "14px" }}>ResearchPal's AI Research Assistant can help you understand the research context behind the sources you are citing. You can discuss a paper, clarify concepts, explore evidence and work through questions about how a source fits into your research before using the free Harvard Citation Generator.</p>
              <p>ResearchPal's Harvard citation workflow is designed to create author-date references and corresponding in-text citations from source information.</p>
            </div>
            <div className="hv-info-card">
              <h3>How the AI Research Assistant Complements Harvard Citations</h3>
              <p style={{ marginBottom: "10px" }}>Use it to:</p>
              <ul className="hv-ai-list">
                <li>Understand research sources before referencing them</li>
                <li>Explore how evidence relates to your research question</li>
                <li>Discuss competing findings or interpretations</li>
                <li>Clarify research concepts and terminology</li>
                <li>Plan how sources can support different parts of your work</li>
                <li>Move from source discovery to structured academic writing</li>
              </ul>
            </div>
          </Reveal>
          <Reveal className="hv-ai-cta">
            <Link to="/ai-agent" className="hv-btn hv-btn-primary">Try AI Research Assistant</Link>
          </Reveal>
        </div>
      </section>

      <section className="hv-section hv-section-soft" id="hv-free-tools">
        <div className="hv-wrap">
          <Reveal className="hv-section-head">
            <span className="hv-eyebrow">More Free Tools</span>
            <h2>Explore ResearchPal's Free Tools</h2>
          </Reveal>

          <div className="hv-free-tools-group">
            <h3 className="hv-free-tools-group-title">ResearchPal Free Tools</h3>
            <Reveal className="hv-feature-grid">
              <Link className="hv-free-tool-card" to="/paraphraser">
                <div><h3>Paraphraser</h3><p>Rewrite sentences and paragraphs while keeping the meaning.</p></div>
                <span className="hv-tool-arrow">→</span>
              </Link>
              <Link className="hv-free-tool-card" to="/ai-agent">
                <div><h3>AI Agent</h3><p>Chat with an AI assistant built for research tasks.</p></div>
                <span className="hv-tool-arrow">→</span>
              </Link>
              <Link className="hv-free-tool-card" to="/search">
                <div><h3>Search Papers</h3><p>Find and explore academic papers across sources.</p></div>
                <span className="hv-tool-arrow">→</span>
              </Link>
              <Link className="hv-free-tool-card" to="/essay-writer">
                <div><h3>Essay Writer</h3><p>Draft structured essays with AI assistance.</p></div>
                <span className="hv-tool-arrow">→</span>
              </Link>
              <Link className="hv-free-tool-card" to="/literature-review">
                <div><h3>Literature Review</h3><p>Summarize and organize sources for your review.</p></div>
                <span className="hv-tool-arrow">→</span>
              </Link>
              <Link className="hv-free-tool-card" to="/paper-insights">
                <div><h3>Paper Insights</h3><p>Get quick, structured insights from any paper.</p></div>
                <span className="hv-tool-arrow">→</span>
              </Link>
            </Reveal>
          </div>

          <div className="hv-free-tools-group">
            <h3 className="hv-free-tools-group-title">Other Free Citation Generators</h3>
            <Reveal className="hv-feature-grid">
              <Link className="hv-free-tool-card" to="/citation-generator/apa">
                <div><h3>APA Citation Generator</h3><p>Format references in APA style instantly.</p></div>
                <span className="hv-tool-arrow">→</span>
              </Link>
              <Link className="hv-free-tool-card" to="/citation-generator/mla">
                <div><h3>MLA Citation Generator</h3><p>Generate MLA in-text and Works Cited entries.</p></div>
                <span className="hv-tool-arrow">→</span>
              </Link>
              <Link className="hv-free-tool-card" to="/citation-generator/chicago">
                <div><h3>Chicago Citation Generator</h3><p>Build Notes-Bibliography or Author-Date citations.</p></div>
                <span className="hv-tool-arrow">→</span>
              </Link>
              <Link className="hv-free-tool-card" to="/citation-generator/bibtex">
                <div><h3>BibTeX Generator</h3><p>Get ready-to-use BibTeX entries for LaTeX.</p></div>
                <span className="hv-tool-arrow">→</span>
              </Link>
              <Link className="hv-free-tool-card" to="/citation-generator/metadata">
                <div><h3>Metadata Lookup</h3><p>View a source's structured details before citing it.</p></div>
                <span className="hv-tool-arrow">→</span>
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="hv-section">
        <div className="hv-wrap">
          <Reveal className="hv-cta-banner">
            <h2>Ready to Generate Your Harvard Citation?</h2>
            <p>Paste a link, DOI, or type the source details, and get a formatted Harvard citation in seconds, with no sign-up required to try.</p>
            <a href="#citation-tool" className="hv-btn hv-btn-primary">Generate My Citation Free</a>
          </Reveal>
        </div>
      </section>

      <section className="hv-section" id="hv-faq">
        <div className="hv-wrap">
          <Reveal className="hv-section-head">
            <span className="hv-eyebrow">Questions Answered</span>
            <h2>Frequently Asked Questions</h2>
            <p>Answers on Harvard referencing and citation, covering setup, formatting, and everyday questions.</p>
          </Reveal>
          <HvFaqList />
        </div>
      </section>
    </div>
  );
};

export default HarvardCitationContent;
